import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { TextInput, Button, Paper, Title, Stack, Alert, Anchor, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { AuthApi } from '../api';
import {
  ForgotPasswordFormSchema,
  type ForgotPasswordFormData,
} from '../schemas/ForgotPasswordForm.schema';
import { ApiError } from '@/shared/api/ApiError';
import { AuthScreenLayout } from './AuthScreenLayout';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(ForgotPasswordFormSchema),
    defaultValues: {
      username: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setError(null);
    try {
      await AuthApi.forgotPassword({ username: data.username });
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to send password reset email');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  if (success) {
    return (
      <AuthScreenLayout>
        <Title ta="center" order={2}>
          Check your email
        </Title>

        <Paper withBorder shadow="md" p={30} mt={30} radius="md">
          <Stack>
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              If an account exists with that username, we've sent password reset instructions
              to its email address.
            </Alert>

            <Text size="sm" c="dimmed" ta="center">
              Didn't receive the email? Check your spam folder or try again.
            </Text>

            <Button variant="light" onClick={() => setSuccess(false)}>
              Try again
            </Button>

            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="center"
              onClick={() => navigate({ to: '/login' })}
            >
              Back to login
            </Anchor>
          </Stack>
        </Paper>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout>
      <Title ta="center" order={2}>
        Forgot password?
      </Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Enter your username and we'll email reset instructions to the address on your account
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}

            <TextInput
              label="Username"
              placeholder="Your username"
              {...form.register('username')}
              error={form.formState.errors.username?.message}
            />

            <Button type="submit" fullWidth loading={form.formState.isSubmitting}>
              Send reset instructions
            </Button>

            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="center"
              onClick={() => navigate({ to: '/login' })}
            >
              Back to login
            </Anchor>
          </Stack>
        </form>
      </Paper>
    </AuthScreenLayout>
  );
}
